import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import {
  User,
  Scissors,
  MapPin,
  Phone,
  Mail,
  Copy,
  Check,
  CheckCircle2,
  ExternalLink,
  MessageSquare,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  MoreVertical,
} from 'lucide-react';
import { StrategyBadge } from '@/components/common/StrategyBadge';
import { WhatsAppMessagePreview } from '@/components/common/WhatsAppMessagePreview';
import { useToast } from '@/components/common/Toast';
import { buildWhatsappUrl } from '@/utils/whatsapp';
import { messageForCampaign } from '@/utils/message';
import {
  getInitials,
  formatClientTenure,
  formatAmountWithBs,
  getCrmClientBadges,
} from '@/utils/crmHelpers';
import { BadgePill } from '@/components/cards/CrmClientCard';
import { useSettingsStore, useCampaignsStore, useCrmStore } from '@/stores';
import { formatDateDDMMYY, getDaysUntil, getDaysAbsent } from '@/utils/date';
import type { CampaignClient } from '@/types/appointments';
import type { CrmClient } from '@/types/crm';

export interface CampaignCardProps {
  client: CampaignClient;
  isProcessed: boolean;
  onMarkProcessed: () => void;
  onOpenDetails?: (client: CampaignClient) => void;
}

export function CampaignCard({
  client,
  isProcessed,
  onMarkProcessed,
  onOpenDetails,
}: CampaignCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copiedType, setCopiedType] = useState<'phone' | 'message' | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const settings = useSettingsStore();
  const statsCache = useCampaignsStore((s) => s.statsCache);
  const segmentMemberships = useCrmStore((s) => s.segmentMemberships);
  const { showToast } = useToast();

  useEffect(() => {
    if (!showMoreMenu) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showMoreMenu]);

  const cached = statsCache[client.customerId];
  const visits =
    typeof cached === 'object' && cached !== null
      ? (cached.appointments ?? client.totalAppointments ?? 0)
      : (typeof cached === 'number' ? cached : (client.completedBookingsCount ?? client.totalAppointments ?? 0));

  const lastStaff = (typeof cached === 'object' ? cached?.staff : undefined) || client.lastStaffMember;
  const lastAppt = (typeof cached === 'object' ? cached?.lastAppointment : undefined) || client.lastAppointmentDate;
  const lastService = (typeof cached === 'object' ? cached?.serviceName : undefined) || client.lastServiceName;
  const lastLocation = (typeof cached === 'object' ? cached?.locationName : undefined) || client.lastLocationName;
  const nextAppt = (typeof cached === 'object' ? cached?.nextAppointment : undefined) || client.nextAppointmentDate;
  const nextStaff = (typeof cached === 'object' ? cached?.nextStaff : undefined) || client.nextStaffMember;
  const nextService = (typeof cached === 'object' ? cached?.nextServiceName : undefined) || client.nextServiceName;
  const daysUntilNext = nextAppt ? getDaysUntil(nextAppt) : ((typeof cached === 'object' ? cached?.daysUntilNext : undefined) ?? client.daysUntilNext);
  const daysAbsent = lastAppt ? getDaysAbsent(lastAppt) : ((typeof cached === 'object' ? cached?.daysAbsent : undefined) ?? client.daysAbsent ?? 0);

  const clientWithCache: CrmClient = {
    ...(client as unknown as CrmClient),
    nextAppointmentDate: nextAppt,
    nextStaffMember: nextStaff,
    nextServiceName: nextService,
    daysUntilNext,
  };

  const activeRate = settings.manualRate || null;

  const totalSalesFormatted = formatAmountWithBs(
    client.totalSales || 0,
    settings.currencyCode || 'EUR',
    activeRate
  );
  const outstandingFormatted = formatAmountWithBs(
    client.outstandingAmount || 0,
    settings.currencyCode || 'EUR',
    activeRate
  );
  const hasDebt = (client.outstandingAmount || 0) > 0.01;

  const tenure = formatClientTenure(client.createdAt);
  const segments = useCrmStore((s) => s.segments);
  const badges = getCrmClientBadges(
    clientWithCache,
    '',
    daysAbsent,
    segmentMemberships,
    settings,
    segments
  );

  const clientPhone = client.phone || '';
  const message = messageForCampaign(client, settings);
  const whatsappUrl = clientPhone ? buildWhatsappUrl(clientPhone, message, settings.whatsappMode) : '';

  const handleCopy = async (text: string, type: 'phone' | 'message', label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedType(type);
      showToast(`${label} copiado al portapapeles`, 'success');
      setTimeout(() => setCopiedType(null), 2000);
    } catch {
      showToast('No se pudo copiar al portapapeles', 'error');
    }
  };

  const hasNextAppt = Boolean(nextAppt);
  const hasLastVisitInfo = Boolean(
    lastAppt || lastStaff || lastService || lastLocation || (!hasNextAppt && daysAbsent > 0)
  );

  return (
    <div
      className={clsx(
        'bg-surface border rounded-xl p-3.5 sm:p-4 transition-all shadow-xs animate-fade-in',
        isProcessed ? 'border-brand-success/40 bg-brand-success/[0.03]' : 'border-border'
      )}
    >
      {/* 1. Header Row */}
      <div className="flex items-start justify-between gap-2.5 mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Avatar */}
          <div
            className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary font-bold flex items-center justify-center text-xs sm:text-sm shrink-0 select-none border border-brand-primary/20"
            aria-hidden="true"
          >
            {getInitials(client.name)}
          </div>

          <div className="min-w-0 flex-1">
            {onOpenDetails ? (
              <button
                type="button"
                onClick={() => onOpenDetails(client)}
                className="font-bold text-text-primary text-sm sm:text-base truncate hover:text-brand-primary cursor-pointer text-left transition-colors flex items-center gap-1.5 max-w-full"
                title="Ver ficha completa del cliente"
              >
                <span className="truncate">{client.name}</span>
                <ExternalLink size={13} className="text-text-muted opacity-60 shrink-0" />
              </button>
            ) : (
              <h3 className="font-bold text-text-primary text-sm sm:text-base truncate">
                {client.name}
              </h3>
            )}
            {tenure && (
              <p className="text-xs text-text-muted mt-0.5 truncate">{tenure}</p>
            )}
          </div>
        </div>

        {/* Dynamic Badges */}
        <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-[50%]">
          <StrategyBadge segment={client.segment} />
          {badges
            .filter((b) => {
              if (client.segment === 'vip' && b.key === 'vip') return false;
              if (client.segment === 'lapsed' && b.key === 'lapsed') return false;
              if (client.segment === 'debtor' && b.key === 'debt') return false;
              return true;
            })
            .map((badge) => (
              <BadgePill key={badge.key} badge={badge} />
            ))}
        </div>
      </div>

      {/* 2. Contextual Next Appointment Block (Green/Blue) */}
      {hasNextAppt && (
        <div className="p-2.5 rounded-lg bg-brand-primary/10 border border-brand-primary/20 text-xs space-y-1 mb-3">
          <div className="flex items-center gap-1.5 font-medium text-brand-primary dark:text-sky-300">
            <Calendar size={12} className="text-brand-primary dark:text-sky-400 shrink-0" />
            <span>
              Próxima cita:{' '}
              <span className="font-semibold">{formatDateDDMMYY(nextAppt)}</span>
              {daysUntilNext !== undefined && daysUntilNext !== null && (
                <span className="text-text-muted text-xs ml-1 font-normal">
                  ({daysUntilNext === 0 ? 'hoy' : daysUntilNext === 1 ? 'mañana' : `en ${daysUntilNext} días`})
                </span>
              )}
            </span>
          </div>
          {(nextService || nextStaff) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-text-secondary pt-0.5">
              {nextService && (
                <div className="flex items-center gap-1.5 truncate">
                  <Scissors size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Servicio:</span> {nextService}
                  </span>
                </div>
              )}
              {nextStaff && (
                <div className="flex items-center gap-1.5 truncate">
                  <User size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Atendido por:</span> {nextStaff}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. Contextual Last Visit Block (Slate) */}
      {hasLastVisitInfo && (
        <div className="p-2.5 rounded-lg bg-surface-secondary/70 border border-border/80 text-xs space-y-1 mb-3">
          <div className="flex items-center gap-1.5 font-medium text-text-primary">
            <Clock size={12} className="text-text-muted shrink-0" />
            <span>
              Última visita:{' '}
              <span className="font-semibold">{lastAppt ? formatDateDDMMYY(lastAppt) : 'Registrada'}</span>
              {daysAbsent !== undefined && daysAbsent !== null && (
                <span className="text-text-muted text-xs ml-1 font-normal">
                  ({daysAbsent > 0 ? `hace ${daysAbsent} días` : 'Reciente'})
                </span>
              )}
            </span>
          </div>
          {(lastService || lastStaff) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-text-secondary pt-0.5">
              {lastService && (
                <div className="flex items-center gap-1.5 truncate">
                  <Scissors size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Servicio:</span> {lastService}
                  </span>
                </div>
              )}
              {lastStaff && (
                <div className="flex items-center gap-1.5 truncate">
                  <User size={11} className="text-brand-primary/70 shrink-0" />
                  <span className="truncate">
                    <span className="text-text-muted">Atendido por:</span> {lastStaff}
                    {lastLocation && (
                      <span className="text-text-muted ml-1">
                        · <MapPin size={10} className="inline mr-0.5" />
                        {lastLocation}
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. Metrics Grid with Dual-Currency */}
      <div className="grid grid-cols-3 gap-2 p-2 bg-surface-secondary border border-border rounded-xl text-center text-xs mb-3">
        <div className="flex flex-col justify-center py-1">
          <span className="text-text-muted block text-xs font-semibold uppercase tracking-wider">
            Visitas
          </span>
          <span className="font-bold text-text-primary text-sm mt-0.5">{visits}</span>
        </div>
        <div className="flex flex-col justify-center py-1 border-x border-border/60 px-1">
          <span className="text-text-muted block text-xs font-semibold uppercase tracking-wider">
            Total Gastado
          </span>
          <span className="font-bold text-text-primary text-sm mt-0.5">
            {totalSalesFormatted.original}
          </span>
          {totalSalesFormatted.bs && (
            <span className="text-xs text-text-muted font-medium mt-0.5 leading-tight">
              ({totalSalesFormatted.bs})
            </span>
          )}
        </div>
        <div className="flex flex-col justify-center py-1 px-1">
          <span className="text-text-muted block text-xs font-semibold uppercase tracking-wider">
            Deuda
          </span>
          <span
            className={clsx(
              'font-bold text-sm mt-0.5',
              hasDebt ? 'text-brand-danger' : 'text-text-secondary'
            )}
          >
            {outstandingFormatted.original}
          </span>
          {outstandingFormatted.bs && (
            <span
              className={clsx(
                'text-xs font-medium mt-0.5 leading-tight',
                hasDebt ? 'text-brand-danger/90' : 'text-text-muted'
              )}
            >
              ({outstandingFormatted.bs})
            </span>
          )}
        </div>
      </div>

      {/* 4. Contact Info Row */}
      {(clientPhone || client.email) && (
        <div className="flex items-center gap-3 py-1.5 px-2.5 bg-surface-secondary/40 border border-border/50 rounded-lg text-xs text-text-secondary mb-3 flex-wrap">
          {clientPhone && (
            <div className="flex items-center gap-1.5">
              <Phone size={12} className="text-text-muted shrink-0" />
              <a
                href={`tel:${clientPhone}`}
                className="hover:text-brand-primary font-mono text-xs transition-colors"
              >
                {clientPhone}
              </a>
            </div>
          )}
          {clientPhone && client.email && (
            <span className="text-border select-none">•</span>
          )}
          {client.email && (
            <div className="flex items-center gap-1.5 truncate">
              <Mail size={12} className="text-text-muted shrink-0" />
              <a
                href={`mailto:${client.email}`}
                className="hover:text-brand-primary truncate text-xs transition-colors"
              >
                {client.email}
              </a>
            </div>
          )}
        </div>
      )}

      {/* 5. Action Toolbar */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        {whatsappUrl && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-3 min-h-[36px] bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366] hover:bg-[#25D366]/25 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
            title="Abrir chat en WhatsApp"
          >
            <MessageSquare size={14} className="shrink-0" />
            <span>WhatsApp</span>
          </a>
        )}

        {onOpenDetails && (
          <button
            type="button"
            onClick={() => onOpenDetails(client)}
            className="inline-flex items-center justify-center gap-1.5 px-2.5 min-h-[36px] bg-surface-secondary text-text-secondary hover:bg-surface-tertiary hover:text-text-primary rounded-lg text-xs font-medium transition-colors cursor-pointer"
            title="Ver ficha del cliente"
          >
            <ExternalLink size={13} className="shrink-0" />
            <span>Ficha</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={clsx(
            'inline-flex items-center justify-center gap-1 px-2.5 min-h-[36px] rounded-lg text-xs font-medium transition-colors cursor-pointer',
            expanded
              ? 'bg-brand-primary/15 text-brand-primary'
              : 'bg-surface-secondary text-text-muted hover:text-text-primary hover:bg-surface-tertiary'
          )}
          title={expanded ? 'Ocultar mensaje sugerido' : 'Ver mensaje sugerido'}
        >
          <MessageSquare size={13} className="shrink-0" />
          <span className="hidden xs:inline sm:inline">Mensaje</span>
          {expanded ? <ChevronUp size={13} className="shrink-0" /> : <ChevronDown size={13} className="shrink-0" />}
        </button>

        {(clientPhone || message) && (
          <div className="relative" ref={moreMenuRef}>
            <button
              type="button"
              onClick={() => setShowMoreMenu((prev) => !prev)}
              className={clsx(
                'inline-flex items-center justify-center w-9 h-9 min-h-[36px] rounded-lg transition-colors cursor-pointer',
                showMoreMenu
                  ? 'bg-surface-tertiary text-text-primary ring-1 ring-border'
                  : 'bg-surface-secondary text-text-muted hover:text-text-primary hover:bg-surface-tertiary'
              )}
              title="Más opciones (Copiar)"
              aria-label="Más opciones"
              aria-expanded={showMoreMenu}
            >
              <MoreVertical size={15} />
            </button>

            {showMoreMenu && (
              <div className="absolute left-0 bottom-full mb-1.5 z-20 min-w-[170px] bg-surface border border-border rounded-xl shadow-lg p-1 animate-fade-in space-y-0.5">
                {clientPhone && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCopy(clientPhone, 'phone', 'Teléfono');
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors text-left cursor-pointer min-h-[36px]"
                  >
                    {copiedType === 'phone' ? (
                      <Check size={14} className="text-brand-success shrink-0" />
                    ) : (
                      <Phone size={14} className="text-text-muted shrink-0" />
                    )}
                    <span>Copiar Teléfono</span>
                  </button>
                )}

                {message && (
                  <button
                    type="button"
                    onClick={() => {
                      handleCopy(message, 'message', 'Mensaje');
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors text-left cursor-pointer min-h-[36px]"
                  >
                    {copiedType === 'message' ? (
                      <Check size={14} className="text-brand-success shrink-0" />
                    ) : (
                      <Copy size={14} className="text-text-muted shrink-0" />
                    )}
                    <span>Copiar Mensaje</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onMarkProcessed}
          className={clsx(
            'ml-auto inline-flex items-center justify-center gap-1.5 px-3 min-h-[36px] rounded-lg text-xs font-medium transition-colors cursor-pointer shrink-0',
            isProcessed
              ? 'bg-brand-success/15 text-brand-success font-semibold border border-brand-success/30'
              : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary border border-border'
          )}
          title={isProcessed ? 'Marcar como pendiente' : 'Marcar como procesado'}
        >
          <CheckCircle2 size={14} className="shrink-0" />
          <span>{isProcessed ? 'Procesado' : 'Marcar'}</span>
        </button>
      </div>

      {/* 6. Expandable Message Preview */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs text-text-secondary animate-fade-in">
          <div className="flex items-center justify-between text-text-muted">
            <span className="font-semibold text-text-primary flex items-center gap-1.5">
              <MessageSquare size={13} className="text-brand-primary" />
              Mensaje contextual de campaña:
            </span>
            {message && (
              <button
                type="button"
                onClick={() => handleCopy(message, 'message', 'Mensaje')}
                className="text-brand-primary hover:underline text-[11px] font-medium flex items-center gap-1 cursor-pointer"
              >
                {copiedType === 'message' ? <Check size={11} /> : <Copy size={11} />}
                <span>Copiar</span>
              </button>
            )}
          </div>
          {message ? (
            <WhatsAppMessagePreview
              message={message}
              variant="bubble"
            />
          ) : (
            <p className="text-text-muted italic">No hay mensaje generado para este cliente.</p>
          )}
        </div>
      )}
    </div>
  );
}
